'use client';

import { useEffect, useLayoutEffect, useRef } from 'react';
import NextImage from 'next/image';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import CmsImage from '@/components/ui/CmsImage';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { LeadershipChairmanMessageProps } from '@/types/leadership';
import { getOptimizedImageUrl } from '@/lib/optimized-strapi-image';

function WordReveal({ text }: { text: string }) {
  const lines = text.split(/<br\s*\/?>/i);

  return (
    <>
      {lines.map((line, lineIndex) => {
        const words = line.split(' ');
        return (
          <span key={lineIndex}>
            {lineIndex > 0 ? <br /> : null}
            {words.map((word, wordIndex) => (
              <span key={wordIndex} data-message-word className="text-[#b9b9b9]">
                {word}
                {wordIndex < words.length - 1 ? ' ' : ''}
              </span>
            ))}
          </span>
        );
      })}
    </>
  );
}

function getSiteHeaderHeight() {
  const cssValue = getComputedStyle(document.documentElement)
    .getPropertyValue('--site-header-height')
    .trim();
  const parsed = Number.parseFloat(cssValue);

  return Number.isFinite(parsed) ? parsed : 80;
}

type MessageMetrics = { overflow: number; scrollDistance: number };

/** Desktop (≥992px): pin the stage and scrub the message scroll + word color. */
function buildDesktopScrollAnimation(
  stage: HTMLElement,
  message: HTMLElement,
  words: HTMLElement[],
  reduceMotion: boolean,
  getMetrics: () => MessageMetrics,
) {
  let timeline: gsap.core.Timeline | null = null;
  let outerRaf = 0;
  let innerRaf = 0;

  const setupDesktop = () => {
    if (reduceMotion) {
      gsap.set(message, { y: 0, clearProps: 'transform' });
      gsap.set(words, { color: '#111111' });
      return;
    }

    const { overflow } = getMetrics();
    const wordStep = 1 / words.length;
    const pinStart = () => `top top+=${getSiteHeaderHeight()}`;

    gsap.set(message, { y: 0, willChange: 'transform' });
    gsap.set(words, { color: '#b9b9b9' });

    if (overflow <= 0) {
      gsap.set(message, { y: 0, clearProps: 'transform,willChange' });
      gsap.to(words, {
        color: '#111111',
        ease: 'none',
        stagger: { each: wordStep },
        scrollTrigger: {
          trigger: stage,
          start: pinStart,
          end: 'bottom top+=60%',
          scrub: 0.5,
        },
      });
      return;
    }

    timeline = gsap
      .timeline({
        scrollTrigger: {
          trigger: stage,
          start: pinStart,
          end: () => `+=${getMetrics().scrollDistance}`,
          pin: true,
          pinSpacing: true,
          scrub: 0.5,
          invalidateOnRefresh: true,
        },
      })
      .to(
        message,
        {
          y: () => -getMetrics().overflow,
          ease: 'none',
          duration: 1,
        },
        0,
      )
      .to(
        words,
        {
          color: '#111111',
          ease: 'none',
          duration: wordStep,
          stagger: { each: wordStep },
          immediateRender: false,
        },
        0,
      );
  };

  outerRaf = window.requestAnimationFrame(() => {
    innerRaf = window.requestAnimationFrame(setupDesktop);
  });

  return () => {
    window.cancelAnimationFrame(outerRaf);
    window.cancelAnimationFrame(innerRaf);
    timeline?.kill();
  };
}

/** Mobile (<992px): color the words as the stage scrolls through the viewport. */
function buildMobileScrollAnimation(
  stage: HTMLElement,
  message: HTMLElement,
  words: HTMLElement[],
  reduceMotion: boolean,
) {
  gsap.set(message, { y: 0, clearProps: 'transform,willChange' });
  if (reduceMotion) {
    gsap.set(words, { color: '#111111' });
    return;
  }

  gsap.set(words, { color: '#b9b9b9' });
  gsap.to(words, {
    color: '#111111',
    ease: 'none',
    stagger: 0.035,
    immediateRender: false,
    scrollTrigger: {
      trigger: stage,
      start: 'top 55%',
      end: 'bottom 55%',
      scrub: true,
    },
  });
}

export default function CoreTeamChairmanMessage({ section }: LeadershipChairmanMessageProps) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const messageViewportRef = useRef<HTMLDivElement | null>(null);
  const messageRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (
      typeof window === 'undefined' ||
      !sectionRef.current ||
      !stageRef.current ||
      !messageViewportRef.current ||
      !messageRef.current
    ) {
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const stage = stageRef.current;
    const messageViewport = messageViewportRef.current;
    const message = messageRef.current;

    const ctx = gsap.context(() => {
      const words = gsap.utils.toArray<HTMLElement>('[data-message-word]', message);
      if (!words.length) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const getMetrics = () => {
        const viewportHeight = messageViewport.clientHeight;
        const messageHeight = message.scrollHeight;

        if (viewportHeight < 1 || messageHeight < 1) {
          return { overflow: 0, scrollDistance: 1 };
        }

        const overflow = Math.max(0, messageHeight - viewportHeight);
        return {
          overflow,
          scrollDistance: overflow > 0 ? overflow : 1,
        };
      };

      const media = gsap.matchMedia();
      media.add('(min-width: 992px)', () =>
        buildDesktopScrollAnimation(stage, message, words, reduceMotion, getMetrics),
      );
      media.add('(max-width: 991px)', () =>
        buildMobileScrollAnimation(stage, message, words, reduceMotion),
      );

      requestAnimationFrame(() => {
        ScrollTrigger.refresh();
      });

      return () => media.revert();
    }, sectionRef);

    return () => ctx.revert();
  }, [section]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let resizeTimer: number | null = null;

    const handleResize = () => {
      if (resizeTimer !== null) {
        window.clearTimeout(resizeTimer);
      }

      resizeTimer = window.setTimeout(() => {
        resizeTimer = null;
        ScrollTrigger.refresh();
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimer !== null) {
        window.clearTimeout(resizeTimer);
      }
    };
  }, []);

  const imageRefreshRafRef = useRef<number | null>(null);

  useEffect(() => {
    if (imageRefreshRafRef.current !== null) {
      window.cancelAnimationFrame(imageRefreshRafRef.current);
    }

    imageRefreshRafRef.current = window.requestAnimationFrame(() => {
      imageRefreshRafRef.current = null;
      ScrollTrigger.refresh();
    });

    return () => {
      if (imageRefreshRafRef.current !== null) {
        window.cancelAnimationFrame(imageRefreshRafRef.current);
      }
    };
  }, [section.media]);

  const parseMessageContent = (htmlContent: string) => {
    return htmlContent
      .split(/<\/p>\s*<p[^>]*>/)
      .map((para) =>
        para
          .replace(/^<p[^>]*>/, '')
          .replace(/<\/p>\s*$/, '')
          .replace(/<(?!br\s*\/?>)[^>]+>/gi, '')
          .trim(),
      )
      .filter((para) => para.length > 0);
  };

  const messageParagraphs = parseMessageContent(section.description);
  const chairmanImageUrl = getOptimizedImageUrl(section.media, 'large');

  return (
    <section ref={sectionRef} className="relative bg-white pb-10 sm:pb-12">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-16 -left-20 z-0 h-[260px] w-[260px] opacity-80 sm:-bottom-20 sm:-left-24 sm:h-[420px] sm:w-[420px] md:-bottom-28 md:-left-32 md:h-[560px] md:w-[560px] lg:h-[640px] lg:w-[640px]"
      >
        <NextImage src="/images/comma_wht.svg" alt="" fill className="object-contain" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-10">
        <div className="mx-auto w-full max-w-[1280px]">
          <div
            ref={stageRef}
            className="box-border flex min-h-0 flex-col lg:relative lg:h-[calc(100svh-var(--site-header-height,5rem)-0.75rem)] lg:w-full"
          >
            <div className="flex min-h-0 flex-1 flex-col lg:pt-5">
              <h2 className="shrink-0 text-center text-3xl font-semibold leading-tight tracking-tight text-[#111111] sm:text-4xl lg:text-[52px]">
                {section.heading}
              </h2>

              <div className="mt-8 flex min-h-0 flex-1 flex-col gap-6 sm:gap-7 lg:mt-5 lg:grid lg:h-full lg:min-h-0 lg:flex-1 lg:grid-cols-[40%_60%] lg:gap-8 lg:pb-1">
                <div className="relative aspect-[4/5] w-full min-h-0 sm:aspect-[5/6] lg:aspect-auto lg:h-full lg:min-h-0">
                  <div className="relative h-full min-h-[280px] overflow-hidden rounded-lg lg:min-h-0">
                    <CmsImage
                      src={chairmanImageUrl}
                      alt={section.heading}
                      fill
                      className="object-cover object-center"
                      sizes="(max-width: 991px) 100vw, 40vw"
                      priority
                      fallbackSrc={MEDIA_PLACEHOLDER}
                    />
                  </div>
                </div>

                <div
                  ref={messageViewportRef}
                  className="relative min-h-0 w-full lg:h-full lg:min-h-0 lg:overflow-hidden"
                >
                  <div ref={messageRef} className="space-y-5 py-1 sm:space-y-6">
                    {messageParagraphs.map((paragraph, index) => (
                      <p key={index} className="text-[15px] leading-7 md:text-[16px] md:leading-8">
                        <WordReveal text={paragraph} />
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
